import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { isStandalone } from "@/lib/pwa";

const b64ToUint8 = (b64: string) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

export type PushState = "unsupported" | "ios-install" | "denied" | "off" | "on" | "loading";

export function usePush() {
  const { user } = useAuth();
  const [state, setState] = useState<PushState>("loading");
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  const refresh = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setState(isIOS && !isStandalone() ? "ios-install" : "unsupported"); return;
    }
    if (Notification.permission === "denied") { setState("denied"); return; }
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    setState(sub ? "on" : "off");
  }, [isIOS]);
  useEffect(() => { refresh(); }, [refresh, user?.id]);

  const enable = useCallback(async () => {
    if (!user) return false;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") { setState(perm === "denied" ? "denied" : "off"); return false; }
    const { data } = await supabase.functions.invoke("send-push", { body: { action: "public_key" } });
    if (!data?.key) return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) });
    const j = sub.toJSON();
    const { error } = await supabase.from("push_subscriptions").upsert(
      { user_id: user.id, endpoint: j.endpoint!, p256dh: j.keys!.p256dh, auth: j.keys!.auth, user_agent: navigator.userAgent.slice(0, 300) },
      { onConflict: "endpoint" } as never,
    );
    if (error) return false;
    setState("on");
    return true;
  }, [user]);

  const disable = useCallback(async () => {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) { await supabase.from("push_subscriptions").delete().eq("endpoint", sub.endpoint); await sub.unsubscribe(); }
    setState("off");
  }, []);

  return { state, enable, disable };
}
